/**
 * Counts the community simulations `generateWholePackageCommunityCombosLite` and
 * `generateByMeasureCommunityCombosLite` will queue for one start action.
 * Both requests receive the same modelUpgradesRun snapshot, so a base case that has
 * not been run is included twice.
 */

const ECM_CATEGORY_ORDER = [
    "ceilingWithAttic",
    "cathedral",
    "wall",
    "expFloor",
    "floorHeader",
    "basementWall",
    "basementSlab",
    "crawlspace",
    "slab",
    "window",
    "skylights",
    "door",
    "airTightness",
    "ventilation",
    "heatingCooling",
    "spaceHeating",
    "secondaryHeating",
    "spaceCooling",
    "primaryHotWater",
    "dwhr",
    "generation",
    "baseLoads",
];

const ENVELOPE_CATEGORIES = [
    "ceilingWithAttic",
    "cathedral",
    "wall",
    "expFloor",
    "floorHeader",
    "basementWall",
    "basementSlab",
    "crawlspace",
    "slab",
    "window",
    "skylights",
    "door",
    "airTightness",
];

const HVAC_CATEGORIES = ["ventilation", "heatingCooling", "spaceHeating", "secondaryHeating", "spaceCooling"];

const DHW_CATEGORIES = ["primaryHotWater", "dwhr"];

const DIRECTION_ORDER = ["S", "SE", "E", "NE", "N", "NW", "W", "SW"];

const WHOLE_PACKAGE_MAX_COMBOS = 500000;

const envelopeSet = new Set(ENVELOPE_CATEGORIES);
const envelopeHvacSet = new Set([...ENVELOPE_CATEGORIES, ...HVAC_CATEGORIES]);
const envelopeHvacDhwSet = new Set([...ENVELOPE_CATEGORIES, ...HVAC_CATEGORIES, ...DHW_CATEGORIES]);

const comboKey = (combo) => combo.map((entry) => `${Object.keys(entry)[0]}=${Object.values(entry)[0]}`).join(";");

const upgradesForPackage = (ecmInputMap, packageId) =>
    ECM_CATEGORY_ORDER.reduce((acc, category) => {
        if (!Object.prototype.hasOwnProperty.call(ecmInputMap, category)) {
            return acc;
        }

        const categoryRows = ecmInputMap[category] || {};
        const selected = Object.keys(categoryRows)
            .filter((upgradeId) => categoryRows[upgradeId])
            .filter((upgradeId) => (categoryRows[upgradeId]?.packageValues?.upgradePackageId || "") === packageId)
            .sort();

        return {
            ...acc,
            [category]: selected.length > 0 ? selected : ["None"],
        };
    }, {});

const wholePackageComboCount = (upgradesToApply) => {
    const optionCounts = Object.values(upgradesToApply).map((options) => options.length);
    if (optionCounts.length === 0 || optionCounts.some((count) => count === 0)) {
        return 0;
    }

    return optionCounts.reduce((product, count) => Math.min(product * count, WHOLE_PACKAGE_MAX_COMBOS), 1);
};

const hasUpgrade = (combo) => combo.some((entry) => Object.values(entry)[0] !== "None");

const subsetCombo = (allowedCategories, packageLookup) =>
    ECM_CATEGORY_ORDER.map((category) => {
        if (!allowedCategories.has(category)) {
            return { [category]: "None" };
        }

        const value = packageLookup[category];
        return { [category]: value == null || value === "None" ? "None" : value };
    });

const byMeasureComboCount = (upgradesToApply) => {
    const categories = Object.keys(upgradesToApply);
    if (categories.length === 0) {
        return 0;
    }

    // By-measure runs use only the first lexicographic package row.
    const packageRow = categories.map((category) => ({ [category]: upgradesToApply[category][0] }));
    const packageLookup = packageRow.reduce((acc, entry) => {
        const category = Object.keys(entry)[0];
        return { ...acc, [category]: Object.values(entry)[0] };
    }, {});
    const individualMeasures = packageRow.filter((entry) => Object.values(entry)[0] !== "None");

    if (individualMeasures.length === 0) {
        return 1;
    }

    const individualCombos = individualMeasures.map((entry) => {
        const category = Object.keys(entry)[0];
        const value = Object.values(entry)[0];
        return ECM_CATEGORY_ORDER.map((candidate) =>
            candidate === category ? { [candidate]: value } : { [candidate]: "None" }
        );
    });
    const seen = new Set(individualCombos.map(comboKey));
    const subsetCount = [envelopeSet, envelopeHvacSet, envelopeHvacDhwSet].reduce((count, allowedCategories) => {
        const combo = subsetCombo(allowedCategories, packageLookup);
        const key = comboKey(combo);
        if (!hasUpgrade(combo) || seen.has(key)) {
            return count;
        }

        seen.add(key);
        return count + 1;
    }, 0);

    return individualMeasures.length + subsetCount;
};

const readyModelDirections = (models) =>
    Object.keys(models)
        .filter((modelId) => models[modelId]?.status === "ready")
        .sort()
        .flatMap((modelId) => {
            const copyDirections = models[modelId]?.countDirection?.copyDirections || {};
            return DIRECTION_ORDER.filter((direction) => (copyDirections[direction] || 0) > 0).map(() => modelId);
        });

export const countCommunityRunsToGenerate = ({
    ecmInputMap = {},
    upgradePackages = [],
    models = {},
    modelUpgradesRun = {},
} = {}) => {
    const packageIds = upgradePackages.map((upgradePackage) => upgradePackage?.id).filter(Boolean);
    if (packageIds.length === 0 || Object.keys(ecmInputMap).length === 0) {
        return 0;
    }

    const upgradesByPackage = packageIds.reduce(
        (acc, packageId) => ({ ...acc, [packageId]: upgradesForPackage(ecmInputMap, packageId) }),
        {}
    );

    return readyModelDirections(models).reduce((total, modelId) => {
        const baseRuns = Object.prototype.hasOwnProperty.call(modelUpgradesRun, modelId) ? 0 : 2;
        const packageRuns = packageIds.reduce((sum, packageId) => {
            if (modelUpgradesRun?.[modelId]?.[packageId]) {
                return sum;
            }

            return sum + wholePackageComboCount(upgradesByPackage[packageId]) + byMeasureComboCount(upgradesByPackage[packageId]);
        }, 0);

        return total + baseRuns + packageRuns;
    }, 0);
};
